import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { WebsocketAction as WebsocketActionMyta } from '@interfaces/webSocketConnectionInfo';
import { LastWebsocketJsonMessageType } from '@clients/ek/interfaces';
import { ChangeAvatarTypes, WebsocketAction } from '@clients/ek/interfaces';
import useGoldenTicket from '@clients/ek/features/goldenTicket/components/GoldenTicket/hooks';
import {
  PoiMediaType,
  SendToBriefcasePropsTypeFull,
} from '@core/dashboard/components/DashboardContent/interfaces';
import { useFeedbackModal } from '../features/feedback/components/FeedbackModal';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { getNotification } from '@components/Notification/helpers';

import { env } from 'env';
import useStyleStudio from '@clients/ek/components/StyleStudio/hook/useStyleStudio';
import { useMytaverseEvent } from '@core/dashboard/providers';

import { default as mytaverseLogger } from '@providers/MytaverseProvider/logger';
import useMagicMirror from '@clients/ek/features/avaturn/hooks/useMagicMirror';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useOverlayContext } from '@providers/OverlayProvider';
import { useTooltipGuideModal } from '@clients/ek/components/TooltipGuide/context/context';
import useCrowningMomentDownload from '@clients/ek/components/StyleStudio/hook/useCrowningMomentDownload';
import { LeaveReasonEnum } from '@clients/ek/components/StyleStudio/LeaveStyleStudioEventNotification/interfaces';
import { useReloadAvatar as useTryOn } from '@clients/ek/components/StyleStudio/ChangeAvatar';
import OVERLAYS from '@constants/overlays';
const { REACT_APP_CLIENT_ENV } = env;
import { useFamiliarization } from '@clients/ek/hooks/useFamiliarization';
import { useClientEventContext } from '@clients/ek/providers/EventProvider/EventProvider';

const useHandleWebsocketMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  useFamiliarization(lastJsonMessage);

  const { gameliftStreamsSessionKey, setShowAvaturnModal } =
    useMytaverseEvent();

  const { setIsSkyLinkActive } = useClientEventContext();
  const { isDebugMode } = useMytaverse();
  const { addToCurrentlyOpenOverlays } = useOverlayContext();

  const { reloadInitialAvatar, reloadAvatar, changeAvatar } = useTryOn();

  const { downloadBackendTicket: handleDownloadGoldenTicket } =
    useGoldenTicket();
  const { setOpen: setOpenFeedbackForm } = useFeedbackModal();

  const showLeaveStyleStudioNotification = useStyleStudio();

  const { crowningMoment, saveUniformedAvatar } = useMagicMirror();

  const handleDownload = useCrowningMomentDownload();

  const { setOpen: setOpenIconsGuide } = useTooltipGuideModal();

  const handleSendToBriefcase = ({
    downloadUrl = '',
    mediaType,
  }: SendToBriefcasePropsTypeFull) => {
    if (
      mediaType === PoiMediaType.WEB_BROWSER &&
      downloadUrl?.includes('feedback')
    ) {
      setOpenFeedbackForm(true);
    }
  };

  const { t: translate } = useTranslation();
  const { showNotification } = useNotification();

  useEffect(() => {
    if (!lastJsonMessage || REACT_APP_CLIENT_ENV !== 'EK') {
      return;
    }

    const { action, payload, sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    const handleMessage = () => {
      switch (action) {
        case WebsocketAction.OpenSkyLink: {
          setIsSkyLinkActive(true);

          break;
        }
        case WebsocketAction.StyleStudioLeave: {
          if (
            [
              LeaveReasonEnum.UnfinishedLegacyWall,
              LeaveReasonEnum.UnfinishedTryOn,
              LeaveReasonEnum.UnfinishedStyleStudio,
            ].includes(payload as LeaveReasonEnum)
          ) {
            showLeaveStyleStudioNotification(
              payload as LeaveReasonEnum,
              reloadInitialAvatar,
            );
          }

          break;
        }

        case WebsocketAction.DownloadGoldenTicket: {
          handleDownloadGoldenTicket();
          break;
        }

        case WebsocketActionMyta.SendToBriefcase: {
          if (lastJsonMessage.mediaType) {
            handleSendToBriefcase({
              downloadUrl: lastJsonMessage.downloadUrl,
              mediaType: lastJsonMessage.mediaType,
            });
          }
          break;
        }

        case WebsocketActionMyta.PreloadAvatar: {
          if (payload === ChangeAvatarTypes.UniformTryOn) {
            changeAvatar(payload);
          }
          break;
        }

        case WebsocketAction.EnteringSSExperience: {
          showNotification(
            getNotification({
              message: translate('notifications.styleStudio.enter'),
              type: NOTIFICATION_TYPES.INFO,
              closeManually: false,
              withReloadButton: false,
              withDismissButton: false,
            }),
          );
          break;
        }

        case WebsocketActionMyta.ReloadAvatar: {
          if (payload === ChangeAvatarTypes.UniformTryOn) {
            reloadAvatar(payload);
          } else if (payload === ChangeAvatarTypes.MagicMirror) {
            setShowAvaturnModal(true);
          } else if (payload === ChangeAvatarTypes.CrowningMoment) {
            crowningMoment();
          }

          break;
        }

        case WebsocketAction.SaveCMAvatar: {
          saveUniformedAvatar();
          break;
        }

        case WebsocketAction.OpenIconsGuide: {
          setOpenIconsGuide(true);
          addToCurrentlyOpenOverlays(OVERLAYS.TOOLTIP_GUIDE);
          break;
        }

        case WebsocketAction.CrowningMoment: {
          if (payload === 'DOWNLOAD_CROWNING_VIDEO') {
            handleDownload();
          }

          break;
        }
      }
    };

    handleMessage();
  }, [
    lastJsonMessage,
    reloadInitialAvatar,
    handleDownloadGoldenTicket,
    gameliftStreamsSessionKey,
    setShowAvaturnModal,
    handleDownload,
    isDebugMode,
    crowningMoment,
    changeAvatar,
  ]);
};

export default useHandleWebsocketMsg;
